import React, { useCallback, useMemo } from 'react';
import { Dropdown, Switch, Text, notify } from '@shootsta/common-react';
import type { Role, WorkspaceSettings } from '../hooks/useWorkspaceSharing';

type WorkspaceAccessSectionProps = {
  roles: Role[];
  workspaceSettings: WorkspaceSettings | null;
  onUpdate: (role: Role, canSearch: boolean) => Promise<any>;
  disabled?: boolean;
};

const ROLE_LABELS: Record<string, string> = {
  NONE: 'No access',
  GUEST: 'View only',
  GUEST_COMMENTER: 'View & Comment'
};

function getRoleOptions(roles: Role[]) {
  return roles.map((role) => ({
    label: ROLE_LABELS[role.name] || role.name,
    value: role.roleId
  }));
}

function WorkspaceAccessSection({
  roles,
  workspaceSettings,
  onUpdate,
  disabled = false
}: WorkspaceAccessSectionProps) {
  const roleOptions = getRoleOptions(roles);
  const currentRole = workspaceSettings?.role;
  const canSearch = workspaceSettings?.canSearch ?? false;

  const selectedOption = useMemo(() => {
    if (currentRole) {
      const found = roleOptions.find((opt) => opt.value === currentRole.roleId);
      if (found) return found;
    }
    return roleOptions.find((opt) => {
      const role = roles.find((r) => r.roleId === opt.value);
      return role?.name === 'NONE';
    });
  }, [currentRole, roleOptions, roles]);

  const handleRoleChange = useCallback(
    async (selected: any) => {
      const selectedRole = roles.find((r) => r.roleId === selected?.value);
      if (!selectedRole) return;

      try {
        await onUpdate(selectedRole, canSearch);
        notify({ content: 'Workspace access updated', type: 'success' });
      } catch {
        notify({ content: 'Failed to update workspace access', type: 'error' });
      }
    },
    [roles, canSearch, onUpdate]
  );

  const handleSearchToggle = useCallback(async () => {
    if (!currentRole) return;

    try {
      await onUpdate(currentRole, !canSearch);
      notify({
        content: `Workspace search ${!canSearch ? 'enabled' : 'disabled'}`,
        type: 'success'
      });
    } catch {
      notify({ content: 'Failed to update workspace search', type: 'error' });
    }
  }, [currentRole, canSearch, onUpdate]);

  if (!roles.length) {
    return null;
  }

  return (
    <div className="video-share-modal__workspace-access">
      <div className="video-share-modal__workspace-access__role">
        <Text noMargin highlighted>
          Workspace members can
        </Text>
        <Dropdown
          className="video-share-modal__workspace-access__dropdown"
          type="BASIC"
          onChange={handleRoleChange}
          defaultValue={selectedOption}
          options={roleOptions}
          disabled={disabled}
        />
      </div>
      <div className="video-share-modal__workspace-access__search">
        <Text noMargin highlighted>
          Enable workspace search
        </Text>
        <Switch
          id="video-share-workspace-search-toggle"
          onChange={handleSearchToggle}
          checked={canSearch}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export default WorkspaceAccessSection;
